// src/pages/Orders/ImplantOrderLabel.js
// Dental implant carton label for the ORDER DETAIL page.
//
// Size: 50mm wide x 165mm tall (portrait). Parts from the top:
//   30mm  top panel     (brand / size / QR)
//   95mm  centre panel  (REF, LOT, QTY, SN, dates, UDI, storage, manufacturer)
//   35mm  bottom panel  (same as the top panel, upside down)
//    5mm  empty strip
// "landscape" turns the whole label 90 degrees (165mm x 50mm) for printers that
// feed the paper that way.

import React from "react";
import { makeQr, qrToPath } from "../../utils/qr";

export const IMPLANT_LABEL_WIDTH_MM = 50;
export const IMPLANT_LABEL_HEIGHT_MM = 165;

/* ================= FIXED TEXTS (change here if needed) ================= */
export const BRAND_TEXT = "OPEN SOURCE";
export const MANUFACTURER_NAME = "Hira-Tech Limited";
export const MANUFACTURER_ADDRESS_1 =
  "188–190 Lythalls Lane ,";
export const MANUFACTURER_ADDRESS_2 = "Coventry CV6 6FT, United Kingdom,";
export const STORAGE_TEXT = "Storage at Room Temperature";
export const DEFAULT_GTIN = "1234567890258";

/* ================= ICON IMAGES (public/implant-icons) =================
   Neeche teeno lines mein apni asal file ka naam (extension samet) likhein. */
const ICON_BASE = `${window.location.origin}${process.env.PUBLIC_URL || ""}/implant-icons`;
export const ICON_MANUFACTURER = `${ICON_BASE}/crowbar.jpg`;
export const ICON_MFG_DATE = `${ICON_BASE}/02_manufacturing_factory.png`;
export const ICON_EXPIRY = `${ICON_BASE}/01_expiry_hourglass.png`;

/* ================= HELPERS ================= */
const pad = (value) => String(value).padStart(2, "0");

export const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const addYears = (isoDate, years) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || "");
  if (!match) return "";
  return `${Number(match[1]) + years}-${match[2]}-${match[3]}`;
};

// 2031-07-04 -> 20310704
const compactDate = (isoDate) => String(isoDate || "").replace(/-/g, "");

export const buildUdiText = ({ gtin, mfgDate, lot, expDate }) =>
  `(01)${gtin}(11)${compactDate(mfgDate)}(10)${lot}(12)${compactDate(expDate)}`;

// "Ø 4.0mm / L10mm, SLA" -> ["Ø 4.0mm / L10mm,", "SLA"]
export const splitSpec = (spec) => {
  const text = String(spec || "").trim();
  const comma = text.lastIndexOf(",");
  if (comma === -1) return [text, ""];
  return [text.slice(0, comma + 1).trim(), text.slice(comma + 1).trim()];
};

const FONT = "Arial, Helvetica, sans-serif";
const BLACK = "#000000";

// Long text is squeezed to fit its space (short text keeps its normal width).
const fit = (text, fontSize, maxLength, avgEm = 0.56) => {
  const natural = String(text || "").length * avgEm * fontSize;
  return natural > maxLength
    ? { textLength: maxLength, lengthAdjust: "spacingAndGlyphs" }
    : {};
};

/* ================= QR CODE ================= */
const QrCode = ({ x, y, size, text, level = "M" }) => {
  if (!text) return null;

  let qr;
  try {
    qr = makeQr(text, level);
  } catch (e) {
    return null;
  }

  return (
    <g transform={`translate(${x} ${y}) scale(${size / qr.size})`}>
      <path d={qrToPath(qr.modules)} fill={BLACK} shapeRendering="crispEdges" />
    </g>
  );
};

/* ================= SYMBOLS ================= */
const LabeledBox = ({ x, y, w, h, text, bold, color = BLACK, fontSize = 1.7 }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill="none" stroke={color} strokeWidth="0.2" />
    <text
      x={x + w / 2}
      y={y + h / 2 + fontSize * 0.36}
      textAnchor="middle"
      fontFamily={FONT}
      fontWeight={bold ? "900" : "400"}
      fontSize={fontSize}
      fill={BLACK}
    >
      {text}
    </text>
  </g>
);

// Icon picture from public/implant-icons
const IconImage = ({ href, x, y, w, h }) => (
  <image
    href={href}
    xlinkHref={href}
    x={x}
    y={y}
    width={w}
    height={h}
    preserveAspectRatio="xMidYMid meet"
  />
);

const TemperatureLimit = ({ x, y, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    {/* tube */}
    <rect x="1.9" y="0" width="1" height="5.9" rx="0.5" fill="none" stroke={BLACK} strokeWidth="0.18" />
    {/* bulb + filled part of the tube */}
    <circle cx="2.4" cy="6.3" r="0.75" fill={BLACK} />
    <rect x="2.05" y="3.1" width="0.7" height="3" fill={BLACK} />
    {/* limit line */}
    <path d="M0.2 5.7 L1.1 5.7 L3.4 2.2 L4.7 1.2" fill="none" stroke={BLACK} strokeWidth="0.2" />
    <text x="4.9" y="1.3" fontFamily={FONT} fontSize="0.8" fill={BLACK}>30°C</text>
    <text x="-0.9" y="6.9" fontFamily={FONT} fontSize="0.8" fill={BLACK}>15°C</text>
  </g>
);

/* ================= TOP / BOTTOM PANEL TEXT (QR is placed separately) ================= */
const PanelTexts = ({ data }) => {
  const [specLine1, specLine2] = splitSpec(data.spec);
  return (
    <g>
      <text x="4" y="11.8" fontFamily={FONT} fontWeight="700" fontSize="3.9" fill={BLACK} {...fit(data.brand, 3.9, 28, 0.72)}>
        {data.brand}
      </text>
      <text x="8.5" y="15.2" fontFamily={FONT} fontWeight="700" fontSize="2.7" fill={BLACK} {...fit(data.level, 2.7, 20, 0.56)}>
        {data.level}
      </text>
      <text x="4" y="21.4" fontFamily={FONT} fontWeight="700" fontSize="3.8" fill={BLACK} {...fit(specLine1, 3.8, 29, 0.58)}>
        {specLine1}
      </text>
      <text x="4" y="26.8" fontFamily={FONT} fontWeight="700" fontSize="3.8" fill={BLACK} {...fit(specLine2, 3.8, 29, 0.58)}>
        {specLine2}
      </text>
    </g>
  );
};

/* ================= THE LABEL =================
   data = {
     brand, level, spec, title, subtitle, compat,
     ref, lot, qty, sn, mfgDate, expDate,
     mount, gtin, topQr
   }
   preview = true draws a light border + grey separators background (screen only).
   landscape = true rotates the label (165mm x 50mm). */
export const ImplantOrderLabel = ({ data, preview = false, landscape = false }) => {
  const udiText = buildUdiText({
    gtin: data.gtin,
    mfgDate: data.mfgDate,
    lot: data.lot,
    expDate: data.expDate,
  });

  const width = landscape ? IMPLANT_LABEL_HEIGHT_MM : IMPLANT_LABEL_WIDTH_MM;
  const height = landscape ? IMPLANT_LABEL_WIDTH_MM : IMPLANT_LABEL_HEIGHT_MM;

  return (
    <svg
      id="implant-order-label"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={`${width}mm`}
      height={`${height}mm`}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: "block", background: "#ffffff", overflow: "visible" }}
    >
      <g transform={landscape ? "matrix(0 1 -1 0 165 0)" : undefined}>
        {preview && (
          <rect
            className="preview-only"
            x="0"
            y="0"
            width={IMPLANT_LABEL_WIDTH_MM}
            height={IMPLANT_LABEL_HEIGHT_MM}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="0.15"
            strokeDasharray="0.8 0.6"
          />
        )}

        {/* ===== separators ===== */}
        <line x1="0" y1="29.7" x2="50" y2="29.7" stroke={BLACK} strokeWidth="0.45" />
        <line x1="0" y1="124.4" x2="50" y2="124.4" stroke={BLACK} strokeWidth="0.45" />
        <line x1="0" y1="159.1" x2="50" y2="159.1" stroke={BLACK} strokeWidth="0.45" />

        {/* ===== TOP PANEL (30mm) ===== */}
        <PanelTexts data={data} />
        <QrCode x={33.9} y={5} size={9.8} text={data.topQr} level="M" />

        {/* ===== CENTRE PANEL (95mm) ===== */}
        <text
          x="24.7"
          y="35"
          textAnchor="middle"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="2.6"
          fill={BLACK}
          {...fit(data.title, 2.6, 46, 0.58)}
        >
          {data.title}
        </text>
        <text
          x="24.7"
          y="38.4"
          textAnchor="middle"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="2.6"
          fill={BLACK}
          {...fit(data.subtitle, 2.6, 46, 0.58)}
        >
          {data.subtitle}
        </text>
        <text
          x="24.7"
          y="41.4"
          textAnchor="middle"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="1.7"
          fill={BLACK}
          {...fit(data.compat, 1.7, 46, 0.56)}
        >
          {data.compat}
        </text>

        {/* REF / LOT / QTY / SN */}
        <LabeledBox x={4.1} y={44.6} w={5.8} h={3.4} text="REF" bold fontSize={2.3} />
        <text x="12.6" y="47.3" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...fit(data.ref, 2.1, 35, 0.58)}>
          {data.ref}
        </text>

        <LabeledBox x={4.1} y={49.2} w={5.8} h={3.4} text="LOT" bold fontSize={2.3} />
        <text x="12.6" y="51.9" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...fit(data.lot, 2.1, 35, 0.58)}>
          {data.lot}
        </text>

        <LabeledBox x={4.1} y={53.8} w={5.8} h={3.4} text="QTY" fontSize={2.3} />
        <text x="12.6" y="56.5" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...fit(data.qty, 2.1, 35, 0.58)}>
          {data.qty}
        </text>

        <LabeledBox x={4.1} y={58.4} w={5.8} h={3.4} text="SN" color="#6b7280" fontSize={2.5} />
        <text x="12.6" y="61.1" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...fit(data.sn, 2.1, 35, 0.58)}>
          {data.sn}
        </text>

        {/* manufacture + expiry dates (icon images) */}
        <IconImage href={ICON_MFG_DATE} x={4.2} y={63.6} w={5.4} h={4.6} />
        <text x="12.6" y="67.4" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK}>
          {data.mfgDate}
        </text>

        <IconImage href={ICON_EXPIRY} x={4.6} y={69.8} w={4.6} h={4.8} />
        <text x="12.6" y="73.6" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK}>
          {data.expDate}
        </text>

        <text
          x="32.2"
          y="73.6"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="2.7"
          fill={BLACK}
          textLength="9"
          lengthAdjust="spacingAndGlyphs"
        >
          Rx only
        </text>

        <text x="10.4" y="80" fontFamily={FONT} fontWeight="700" fontSize="2.1" fill={BLACK} {...fit(data.mount, 2.1, 36, 0.56)}>
          {data.mount}
        </text>

        {/* UDI */}
        <LabeledBox x={6.4} y={88.4} w={5.8} h={3.4} text="UDI" fontSize={2.3} />
        <QrCode x={13.4} y={84.4} size={11.4} text={udiText} level="L" />
        <text x="26.2" y="86.7" fontFamily={FONT} fontWeight="700" fontSize="1.8" fill={BLACK} {...fit(`(01)${data.gtin}`, 1.8, 23, 0.58)}>
          {`(01)${data.gtin}`}
        </text>
        <text x="26.2" y="89.5" fontFamily={FONT} fontWeight="700" fontSize="1.8" fill={BLACK} {...fit(`(11)${compactDate(data.mfgDate)}`, 1.8, 23, 0.58)}>
          {`(11)${compactDate(data.mfgDate)}`}
        </text>
        <text x="26.2" y="92.3" fontFamily={FONT} fontWeight="700" fontSize="1.8" fill={BLACK} {...fit(`(10)${data.lot}`, 1.8, 23, 0.58)}>
          {`(10)${data.lot}`}
        </text>
        <text x="26.2" y="95.1" fontFamily={FONT} fontWeight="700" fontSize="1.8" fill={BLACK} {...fit(`(12)${compactDate(data.expDate)}`, 1.8, 23, 0.58)}>
          {`(12)${compactDate(data.expDate)}`}
        </text>

        {/* storage temperature */}
        <TemperatureLimit x={35.6} y={99.2} scale={1.3} />
        <text
          x="22.6"
          y="112.6"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="2"
          fill={BLACK}
          textLength="26"
          lengthAdjust="spacingAndGlyphs"
        >
          {STORAGE_TEXT}
        </text>

        {/* manufacturer
            Font bara (1.9) kiya hai. Aakhri line ki baseline y=123.5 hai, jabke
            separator line y=124.4 par hai, is liye text line ke bilkul upar aata hai.
            fit() sirf lamba text dabata hai, chhota text apni asal shakal mein rehta hai. */}
        <IconImage href={ICON_MANUFACTURER} x={3} y={117.2} w={5.4} h={5.4} />
        <text
          x="9.2"
          y="118.5"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="1.9"
          fill={BLACK}
          {...fit(MANUFACTURER_NAME, 1.9, 38, 0.58)}
        >
          {MANUFACTURER_NAME}
        </text>
        <text
          x="9.2"
          y="121"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="1.9"
          fill={BLACK}
          {...fit(MANUFACTURER_ADDRESS_1, 1.9, 38, 0.58)}
        >
          {MANUFACTURER_ADDRESS_1}
        </text>
        <text
          x="9.2"
          y="123.5"
          fontFamily={FONT}
          fontWeight="700"
          fontSize="1.9"
          fill={BLACK}
          {...fit(MANUFACTURER_ADDRESS_2, 1.9, 38, 0.58)}
        >
          {MANUFACTURER_ADDRESS_2}
        </text>

        {/* ===== BOTTOM PANEL (35mm): same texts, upside down ===== */}
        <g transform="rotate(180 25.4 79.05)">
          <PanelTexts data={data} />
        </g>
        <QrCode x={3.6} y={138.8} size={9.8} text={data.topQr} level="M" />
      </g>
    </svg>
  );
};

export default ImplantOrderLabel;